import { Component, NgZone, inject, signal } from '@angular/core';

import 'dayjs/plugin/localeData';
import 'dayjs/locale/cs';
import 'dayjs/locale/en';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { IdleService } from 'src/services/idle.service';
import { NewInvestmentProgressService } from 'src/services/clear-investment-progress.service';
import { LoginService } from 'src/services/login.service';
import * as CookieConsent from 'vanilla-cookieconsent';
import { LOCAL_STORAGE_KEYS, LocalStorageService } from 'src/services/local-storage.service';
import { Capacitor } from '@capacitor/core';
dayjs.extend(utc);
dayjs.extend(isSameOrBefore);
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import { from, take, tap } from 'rxjs';
import { PosthogService } from 'src/services/posthog.service';
import { DeviceService } from 'src/services/device.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfigService } from 'src/services/json-config.service';
import { NativeSafeZoneService } from 'src/services/native-safe-zone.service';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import { LoggerService } from 'src/services/logger.service';
import { PushNotificationsService } from 'src/services/push-notifications.service';
import { UserDataService } from 'src/data-store/user-data.service';
import { PhoneNativeService } from 'src/services/phone-native.service';
import { EApiDevicePlatforms } from 'src/models/app.model';
import { RedirectService } from 'src/services/redirect.service';
import { Keyboard } from '@capacitor/keyboard';
import { AppStore } from 'src/data-store/app/app.store';
import { ThemeService } from 'src/services/theme.service';

@Component({
  selector: 'liberro-root',
  templateUrl: './app.html',
  standalone: false,
  styleUrls: ['./app.less'],
})
export class AppComponent {
  public phoneNativeService = inject(PhoneNativeService);
  public deviceService = inject(DeviceService);
  public nativeSafeZoneService = inject(NativeSafeZoneService);
  private posthogService = inject(PosthogService);
  private localStorageService = inject(LocalStorageService);
  private translate = inject(TranslateService);
  private configService = inject(ConfigService);
  private loggerService = inject(LoggerService);
  private userDataService = inject(UserDataService);
  private zone = inject(NgZone);
  private redirectService = inject(RedirectService);
  private pushNotificationsService = inject(PushNotificationsService);
  private appStore = inject(AppStore);
  private idleService = inject(IdleService);
  private newInvestmentProgressService = inject(NewInvestmentProgressService);
  private loginService = inject(LoginService);
  private themeService = inject(ThemeService);


  private appVersionCheckDevice: EApiDevicePlatforms | null = null;
  protected readonly viewNeedUpdateOverlay = this.appStore.viewNeedUpdateOverlay;

  public isIosNative = signal(false);

  public constructor() {
    if(this.deviceService.isNativePlatform) {
      this.initNativeAppWorkarounds();
    }
    this.initCookies();
  }

  private initPosthog(): void {
    if(Capacitor.getPlatform() === 'ios') {
      // iOS additional tracking request
      from(AppTrackingTransparency.requestPermission()).pipe(
        take(1),
        tap(response => {
          if(response && response.status === 'authorized') {
            this.posthogService.init();
          }
        }),
      );
    } else {
      this.posthogService.init();
    }
  }

  private initCookieBar(): void {
    CookieConsent.run({
        onConsent: (data) => {
          if (data.cookie.categories.includes(LOCAL_STORAGE_KEYS.COOKIES_ANALYTICS_CATEGORY)) {
            this.initPosthog();
          }
          if(this.deviceService.isNativePlatform) {
            this.localStorageService.setItem({
              key: LOCAL_STORAGE_KEYS.NATIVE_COOKIE_STORAGE_KEY,
              value: localStorage.getItem(LOCAL_STORAGE_KEYS.COOKIE_STORAGE_KEY) ?? ''
            });
          }
        },
        onChange: (data) => {
          if (data.cookie.categories.includes(LOCAL_STORAGE_KEYS.COOKIES_ANALYTICS_CATEGORY)) {
            this.initPosthog();
          }
          if(this.deviceService.isNativePlatform) {
            this.localStorageService.setItem({
              key: LOCAL_STORAGE_KEYS.NATIVE_COOKIE_STORAGE_KEY,
              value: localStorage.getItem(LOCAL_STORAGE_KEYS.COOKIE_STORAGE_KEY) ?? ''
            });
          }
        },
        cookie: {
          name: 'bwm-v2',
          useLocalStorage: this.deviceService.isNativePlatform,
        },
        revision: 1,
        disablePageInteraction: true,
        autoClearCookies: true,
        mode: 'opt-in',
        guiOptions: {
          consentModal: {
              layout: 'bar',
              position: 'bottom center',
              equalWeightButtons: false,
          },
          preferencesModal: {
              layout: 'bar wide',
              position: 'left',
              equalWeightButtons: false,
          }
        },
        categories: {
          necessary: {
            readOnly: true
          },
          analytics: {
            readOnly: false
          },
          advertisement: {
            readOnly: false
          }
        },
        language: {
          default: 'cs',
          translations: {
            cs: {
              consentModal: {
                  title: '',
                  description: this.translate.instant('cookieConsent.consentDescription1') + this.configService.getConfig(EnvironmentKeys.cookieConsentDocumentLink) + this.translate.instant('cookieConsent.consentDescription2'),
                  acceptAllBtn: this.translate.instant('cookieConsent.consentAcceptAllBtn'),
                  acceptNecessaryBtn: this.translate.instant('cookieConsent.consentAcceptNecessaryBtn'),
                  showPreferencesBtn: this.translate.instant('cookieConsent.consentShowPreferencesBtn'),
              },
              preferencesModal: {
                  title: this.translate.instant('cookieConsent.preferencesTitle'),
                  savePreferencesBtn: this.translate.instant('cookieConsent.preferencesSavePreferencesBtn'),
                  acceptAllBtn: this.translate.instant('cookieConsent.preferencesAcceptAllBtn'),
                  closeIconLabel: this.translate.instant('cookieConsent.preferencesCloseIconLabel'),
                  sections: [
                      {
                          title: '',
                          description: this.translate.instant('cookieConsent.expandedDescription1') + this.configService.getConfig(EnvironmentKeys.cookieConsentDocumentLink) + this.translate.instant('cookieConsent.expandedDescription2'),
                      }, {
                          title: this.translate.instant('cookieConsent.necessaryCookies'),
                          description: this.translate.instant('cookieConsent.necessaryCookiesDescription'),
                          linkedCategory: 'necessary',
                      }, {
                          title: this.translate.instant('cookieConsent.analyticsCookies'),
                          description: this.translate.instant('cookieConsent.analyticsCookiesDescription'),
                          linkedCategory: LOCAL_STORAGE_KEYS.COOKIES_ANALYTICS_CATEGORY,
                      }
                  ]
              },
          },
          }
        },
        onModalReady: (params) => {
          if(params.modalName === 'preferencesModal') {
            params.modal.style.marginTop = this.nativeSafeZoneService.paddings.top + 'px';
            params.modal.style.paddingBottom = this.nativeSafeZoneService.paddings.bottom + 'px';
          }
        }
    });
  }

  private initCookieFromStorage(ccValue: string): void {
    try {
      if(ccValue && ccValue.length) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.COOKIE_STORAGE_KEY, ccValue);
      }
      this.initCookieBar();
    } catch (error) {
      this.loggerService.error('Error parsing Cookie preferences from storage.', error);
    }
  }

  private initCookies(): void {
    if(this.deviceService.isNativePlatform) {
      this.localStorageService.getItem({ key: LOCAL_STORAGE_KEYS.NATIVE_COOKIE_STORAGE_KEY }).pipe(take(1)).subscribe({
        error: this.initCookieFromStorageError.bind(this),
        next: this.initCookieFromStorage.bind(this),
      });
    } else {
      this.initCookieBar();
    }
  }

  private initNativeAppWorkarounds(): void {
    if(Capacitor.getPlatform() === 'ios') {
      this.isIosNative.set(true);
      Keyboard.setAccessoryBarVisible({ isVisible: true });
      this.appVersionCheckDevice = EApiDevicePlatforms.IOS;
    }
    if(Capacitor.getPlatform() === 'android') {
      this.appVersionCheckDevice = EApiDevicePlatforms.ANDROID;
    }

    this.phoneNativeService.initDeeplinksWatcher();
    this.phoneNativeService.enableScreenBlurOnBackground();

    if(this.userDataService.loggedUser()) {
      this.phoneNativeService.biometricAuth();
      this.pushNotificationsService.initNotifications();
    }

    this.phoneNativeService.isVersionValid(this.appVersionCheckDevice).subscribe({
      next: (validVersion) => {
        this.zone.run(() => this.appStore.displayViewNeedUpdateOverlay(!validVersion));
      }
    });

  }

  public goToAppStore(): void {
    if (this.viewNeedUpdateOverlay() && this.appVersionCheckDevice)
      this.redirectService.goToAppStore(this.appVersionCheckDevice);
    else {
      this.loggerService.error(`Invalid state for go to app store to upgrade! viewNeedUpdateOverlay: ${this.viewNeedUpdateOverlay()}, appVersionCheckDevice: ${this.appVersionCheckDevice}`);
    }
  }

  private initCookieFromStorageError(error: unknown): void {
    this.loggerService.error('Error getting Cookie preferences from storage.', error);
  }
}
